/**
 * Global EULA editor (admin scope). Ports the admin half of the publisher
 * dashboard's views/eula into admin.nurev.io (audit 2026-06-09).
 *
 * The dashboard used react-draft-wysiwyg (draft-js); the admin console is
 * intentionally dependency-light, so this edits the EULA HTML source
 * directly with a live preview alongside. The preview renders in a fully
 * sandboxed iframe (sandbox="" → no scripts, no same-origin) so authored
 * markup can never reach into the console. The EULA is a single global
 * AppConfig record served by /settings/eula.
 */
import { ReactNode, useEffect, useState } from 'react';
import { AlertCircle, Save, Undo2 } from 'lucide-react';
import { useEula, useUpdateEula } from '@/hooks/useEula';
import { ActionButton, Card, Skeleton } from '@/components/ui/atoms';

function previewDoc(html: string): string {
  const body = html || '<p style="color:#999">Nothing to preview.</p>';
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
    body{font-family:system-ui,-apple-system,sans-serif;color:#111;background:#fff;margin:0;padding:20px;line-height:1.55;font-size:14px}
    a{color:#6c4fd4}h1,h2,h3,h4{line-height:1.25}
  </style></head><body>${body}</body></html>`;
}

const PaneLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
    {children}
  </span>
);

export function EulaScreen() {
  const { data, isLoading, error } = useEula();
  const update = useUpdateEula();
  const [draft, setDraft] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Seed the draft once the EULA first loads. Don't re-seed on background
  // refetches so in-flight edits aren't clobbered; after a save the cache
  // is updated to the persisted value, which clears the dirty state.
  useEffect(() => {
    if (data !== undefined && !initialized) {
      setDraft(data ?? '');
      setInitialized(true);
    }
  }, [data, initialized]);

  const original = data ?? '';
  const dirty = initialized && draft !== original;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-nurev-text">EULA</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            The platform-default end-user license agreement bundled into installers.
            Edits apply globally. Accepts HTML.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-300">Unsaved changes</span>}
          <ActionButton
            variant="neutral"
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={() => setDraft(original)}
          >
            <Undo2 className="mr-1 inline h-3.5 w-3.5" /> Revert
          </ActionButton>
          <ActionButton
            variant="primary"
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={() => update.mutate(draft)}
          >
            <Save className="mr-1 inline h-3.5 w-3.5" />
            {update.isPending ? 'Saving…' : 'Save'}
          </ActionButton>
        </div>
      </div>

      {error ? (
        <Card className="py-10 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
          <p className="text-sm text-nurev-muted">Couldn't load the EULA.</p>
        </Card>
      ) : isLoading || !initialized ? (
        <Skeleton className="h-[460px] w-full" />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="p-0">
            <div className="border-b border-nurev-border px-4 py-2.5">
              <PaneLabel>Source (HTML)</PaneLabel>
            </div>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              spellCheck={false}
              className="h-[460px] w-full resize-none rounded-b-xl bg-transparent p-4 font-mono text-xs leading-relaxed text-nurev-text outline-none"
            />
          </Card>
          <Card className="p-0">
            <div className="flex items-center justify-between border-b border-nurev-border px-4 py-2.5">
              <PaneLabel>Preview</PaneLabel>
              <span className="text-[10px] text-nurev-muted">
                {draft.length.toLocaleString()} chars
              </span>
            </div>
            <iframe
              title="EULA preview"
              sandbox=""
              srcDoc={previewDoc(draft)}
              className="h-[460px] w-full rounded-b-xl border-0 bg-white"
            />
          </Card>
        </div>
      )}
    </div>
  );
}
